import { useCallback, useSyncExternalStore } from "react";

export type TableDensity = "comfortable" | "compact";

const STORAGE_KEY = "senseguard:table-density";
const EVENT = "senseguard:table-density-change";

function readDensity(): TableDensity {
  if (typeof window === "undefined") return "compact";
  return window.localStorage.getItem(STORAGE_KEY) === "comfortable" ? "comfortable" : "compact";
}

/**
 * Reflect the density onto <html> as the library's `data-h2j-density`, so a
 * table composed from the `Table` primitives and the virtualized rows
 * (`--tbl-row-min`, `--tbl-cell-py` in index.css) follow the preference too;
 * a DataTable gets it through its `density` prop.
 */
export function applyDensityToDocument(density: TableDensity = readDensity()) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-h2j-density", density);
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** Persisted comfortable/compact table density, shared across every table (default: compact). */
export function useTableDensity() {
  const density = useSyncExternalStore(subscribe, readDensity, () => "compact" as TableDensity);

  const setDensity = useCallback((next: TableDensity) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    applyDensityToDocument(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return { density, setDensity };
}
